import { sameNumber } from "@/lib/phone-number";
import type { AgentLine, AgentState, ManualCall, ManualCallStage } from "@/lib/types";

/**
 * A number the rep dials by hand, placed through their campaign (ADR-047 in the platform repo):
 * the rules the phone applies before it asks, and the words it uses while the call is placed
 * and after it fails.
 *
 * Pure, and kept out of the components for the reason every rule in `lib/acd.ts` is: what a rep
 * is shown mid-call is decided here, and a rule that can only be exercised with a trunk and a
 * lead's phone is a rule nobody re-checks. `test/manual-call.test.ts` does.
 */

/**
 * Where a rep may place a call through a campaign from, overall and on its line: between calls
 * — Ready, on a break (the ACD takes them off it for the call and puts them back on it after),
 * or in wrap-up, which the call ends (ADR-068). core-api's `DIAL_FROM`.
 */
const DIAL_FROM: ReadonlySet<string> = new Set(["available", "paused", "wrapup"]);
/** The same, while the ACD holds the rep for their last call's outcome (see `dialCampaigns`). */
const IN_THE_WINDOW: ReadonlySet<string> = new Set([...DIAL_FROM, "disposition"]);

/**
 * The campaigns an outside number the rep dials goes through right now: the lines they are
 * signed in to whose campaign it is, while they are between calls (`DIAL_FROM`).
 *
 * The same rule as core-api's `_dial_campaigns`, on the same record, so a call the phone offers
 * to place through a campaign is one core-api will accept. A rep on a call, being rung, or with
 * a dial of their own still going gets none: the answered lead is pinned to the rep who dialed
 * them, and a rep the ACD would not ring is a lead who answers to nobody.
 *
 * The disposition window is the one place the two part on purpose. The phone sends the call on
 * and core-api refuses it with "Choose the outcome of your last call first.", which every
 * surface shows: the window holds the next call until the outcome is in, rather than letting it
 * out from the phone as a plain call with none of the campaign's rules on it.
 */
export function dialCampaigns(me: AgentState | null | undefined): AgentLine[] {
  const record = me?.state;
  if (!me || !record) return [];
  const from = record.state === "disposition" ? IN_THE_WINDOW : DIAL_FROM;
  if (!from.has(record.state)) return [];
  const states = record.queueStates ?? {};
  const seen = new Set<string>();
  const lines: AgentLine[] = [];
  for (const line of me.lines) {
    if (!line.campaign_id || seen.has(line.campaign_id)) continue;
    if (!from.has(states[line.name] ?? "")) continue;
    seen.add(line.campaign_id);
    lines.push(line);
  }
  return lines.sort((a, b) => (a.campaign_name ?? "").localeCompare(b.campaign_name ?? ""));
}

/**
 * How the phone places `destination`: through a campaign, when it is an outside number and the
 * rep is between calls on one — the dialer originates it and the ACD brings the answered lead
 * back to the rep's phone, in the call's room (ADR-047, ADR-068) — or from the rep's own phone,
 * straight to the switch: an extension, always; an outside number when the rep is signed in to
 * no campaign, or is on a call. `backToBreak`: the rep is on a break, and goes back on it after.
 */
export type DialPath =
  | { via: "campaign"; campaigns: AgentLine[]; backToBreak: boolean }
  | { via: "phone"; reason: "extension" | "not-ready" | "empty" };

export function dialPath(
  destination: string,
  me: AgentState | null | undefined,
  isPstn: (raw: string) => boolean,
): DialPath {
  if (!destination.trim()) return { via: "phone", reason: "empty" };
  if (!isPstn(destination)) return { via: "phone", reason: "extension" };
  const campaigns = dialCampaigns(me);
  if (campaigns.length === 0) return { via: "phone", reason: "not-ready" };
  return { via: "campaign", campaigns, backToBreak: me?.state?.state === "paused" };
}

/**
 * The campaign a dial goes through when the rep has not chosen one: the only one they can call
 * through, or the one they chose last time if they still can, else nothing — the picker
 * asks. core-api makes the same choice on its side when the console sends none, plus one it can
 * make and the console cannot: the campaign whose list holds the lead.
 */
export function chosenCampaign(
  campaigns: readonly AgentLine[],
  remembered: string | null,
): AgentLine | null {
  if (campaigns.length === 1) return campaigns[0]!;
  return campaigns.find((c) => c.campaign_id === remembered) ?? null;
}

/**
 * What the Dial box says under the number, so the rep knows where a call will go before they
 * press Call — and knows why a colleague's extension does not go the same way.
 */
export function dialPathText(path: DialPath, chosen: AgentLine | null, asRep = false): string | null {
  switch (path.via) {
    case "campaign": {
      const after = path.backToBreak ? " You go back on your break after." : "";
      // A rep names no campaign: core-api picks it (ACL-652).
      if (asRep) return `Their answer rings your phone.${after}`;
      return chosen
        ? `Goes out through ${chosen.campaign_name ?? chosen.name}. Their answer rings your phone.${after}`
        : "Choose the campaign this call goes out through.";
    }
    case "phone":
      return path.reason === "extension" ? "Rings your colleague's phone directly." : null;
  }
}

// --- following the call ------------------------------------------------------------------------------

/** The stages in the order a call passes through them, for the progress steps. */
export const STAGES: readonly ManualCallStage[] = [
  "requested",
  "dialing",
  "ringing",
  "answered",
  "connected",
  "ended",
];

/** Whether the call is still on its way: the dock shows the progress card, with Cancel. */
export function inProgress(stage: ManualCallStage): boolean {
  return stage !== "connected" && stage !== "ended";
}

/**
 * The console's own ringback sounds from the far end's first ring until the rep is connected:
 * never before the switch has heard back from the carrier, and on through "answered", when the
 * lead has picked up and the ACD is ringing the rep's phone with them. To the rep that is one
 * outbound call ringing out, so it sounds like one; the phone's incoming-call bell stays quiet
 * for that ring (`expectCall`), and the ringback stops the moment their own leg is up.
 */
export function playsRingback(stage: ManualCallStage): boolean {
  return stage === "ringing" || stage === "answered";
}

/**
 * The three steps the progress card draws, and which is under way. "Answered" and "connected"
 * are one step to the rep — the moment the lead picks up their own phone rings and the ACD joins
 * the two — and `requested` is the first step already begun.
 */
export interface ProgressStep {
  label: string;
  state: "done" | "current" | "todo";
}

export function progressSteps(stage: ManualCallStage): ProgressStep[] {
  const order = ["placing", "ringing", "answered"] as const;
  const at = stage === "requested" || stage === "dialing" ? 0 : stage === "ringing" ? 1 : 2;
  const labels = { placing: "Placing the call", ringing: "Ringing", answered: "Answered" };
  return order.map((key, i) => ({
    label: labels[key],
    state: i < at ? "done" : i === at ? "current" : "todo",
  }));
}

/** One line under the steps, read aloud as it changes: what is happening right now. */
export function stageText(stage: ManualCallStage): string {
  switch (stage) {
    case "requested":
      return "Placing the call…";
    case "dialing":
      return "Reaching the carrier…";
    case "ringing":
      return "Ringing…";
    case "answered":
      return "They answered. Bringing them to your phone…";
    case "connected":
      return "Connected.";
    case "ended":
      return "The call has ended.";
  }
}

/**
 * How a call that never connected ended, in the rep's words.
 *
 * The switch reports a SIP hangup cause; the dialer writes its own four for a call that never
 * left (ADR-047). Each becomes a title a rep can act on and one sentence of why, with the raw
 * cause kept for the one that has no better name — a rep who reads `UNALLOCATED_NUMBER` off
 * the card can tell an admin exactly what the carrier said.
 */
export type FailureKind =
  | "no_answer"
  | "busy"
  | "cancelled"
  | "not_in_service"
  | "blocked"
  | "unanswered_by_rep"
  | "failed";

export interface FailureText {
  kind: FailureKind;
  title: string;
  detail: string;
  /** The cause as the switch or the dialer spelled it, when the title does not already say it. */
  cause: string | null;
  /** Whether trying the same number again could go differently. */
  retryable: boolean;
}

export function failureText(call: Pick<ManualCall, "outcome" | "hangup_cause">): FailureText {
  const cause = (call.hangup_cause ?? "").toUpperCase();
  // The lead picked up and waited the manual window for this phone, which never answered: the
  // platform ended the call rather than leave them on a silent line. Whatever the switch wrote
  // as the cause, the rep needs to hear that it was their phone.
  if (call.outcome === "abandoned") {
    return {
      kind: "unanswered_by_rep",
      title: "They answered, but your phone didn't",
      detail:
        "Your phone did not pick up in time, so the call was ended. Check that it can answer, then try again.",
      cause: null,
      retryable: true,
    };
  }
  switch (cause) {
    case "ORIGINATOR_CANCEL":
      return {
        kind: "cancelled",
        title: "Cancelled",
        detail: "You hung up before they answered.",
        cause: null,
        retryable: true,
      };
    case "NO_ANSWER":
    case "NO_USER_RESPONSE":
    case "ALLOTTED_TIMEOUT":
      return {
        kind: "no_answer",
        title: "No answer",
        detail: "They didn't pick up.",
        cause: null,
        retryable: true,
      };
    case "USER_BUSY":
      return {
        kind: "busy",
        title: "Busy",
        detail: "The line was busy. Try again in a minute.",
        cause: null,
        retryable: true,
      };
    case "UNALLOCATED_NUMBER":
    case "NO_ROUTE_DESTINATION":
    case "INVALID_NUMBER_FORMAT":
      return {
        kind: "not_in_service",
        title: "Number not in service",
        detail: "The carrier says this number does not exist. Check it and try again.",
        cause,
        retryable: false,
      };
    case "CALL_REJECTED":
      return {
        kind: "failed",
        title: "Call rejected",
        detail: "The far end refused the call.",
        cause,
        retryable: true,
      };
    case "DNC":
      return {
        kind: "blocked",
        title: "On the do-not-call list",
        detail: "This number cannot be called.",
        cause: null,
        retryable: false,
      };
    case "DAILY_CAP":
      return {
        kind: "blocked",
        title: "Called enough for today",
        detail:
          "This number has already been called as many times as it can be today. It can be called again tomorrow.",
        cause: null,
        retryable: false,
      };
    case "NO_TRUNK":
      return {
        kind: "blocked",
        title: "No line out",
        detail: "There is no trunk to call out on. Ask an admin.",
        cause: null,
        retryable: false,
      };
    case "NO_CALLER_ID":
      return {
        kind: "blocked",
        title: "No number to show",
        detail: "There is no caller ID to present for this call. Ask an admin.",
        cause: null,
        retryable: false,
      };
    default:
      if (call.outcome === "no_answer") {
        return {
          kind: "no_answer",
          title: "No answer",
          detail: "They didn't pick up.",
          cause: cause || null,
          retryable: true,
        };
      }
      return {
        kind: "failed",
        title: "Call failed",
        detail: "The carrier could not connect the call.",
        cause: cause || null,
        retryable: true,
      };
  }
}

/**
 * Whether a call that has ended is one the rep talked on: then the phone's own call view has
 * shown it, the line's outcome window follows, and the card has nothing to add.
 */
export function wasConnected(call: Pick<ManualCall, "bridged_at" | "outcome">): boolean {
  return call.bridged_at !== null || call.outcome === "human_contact";
}

/**
 * The ringing call on this phone that is the manual dial coming back, or null. Two ways to know
 * it: the ACD's offer names the lead leg it is for (`callerChannelId`), which is the call id the
 * rep asked for — and, before the rep's record has caught up with the ring, the call presents
 * the very number the rep dialed (`number`), which nothing else on this phone does while that
 * dial is in progress. The phone answers it without a click — the rep placed this call —
 * whatever auto-answer says. Never over another call: two un-held calls means both far ends
 * hear the rep.
 */
export function ownCallToAnswer(
  callId: string | null,
  callerChannelId: string | null | undefined,
  calls: readonly { id: string; direction: "inbound" | "outbound"; state: string; remote?: string }[],
  acdCallId: string | null,
  number: string | null = null,
): string | null {
  if (!callId) return null;
  const only = calls.length === 1 ? calls[0]! : null;
  if (!only || only.direction !== "inbound" || only.state !== "ringing") return null;
  const named = callerChannelId === callId && acdCallId !== null && acdCallId === only.id;
  const presented = number !== null && sameNumber(only.remote, number);
  return named || presented ? only.id : null;
}

/** Whole seconds the call has been going, from when it was requested. */
export function secondsSince(startedAt: string | null, now: number): number {
  if (!startedAt) return 0;
  const at = Date.parse(startedAt);
  return Number.isNaN(at) ? 0 : Math.max(0, Math.floor((now - at) / 1000));
}

/**
 * The lead a call placed by hand was for, when it dialed another number than theirs: their
 * emergency contact, called from their record (ACL-382). Call again sends it back, or the second
 * try would be filed on whichever record has that number. Null for a call to the lead's own
 * number, which core-api finds by the number as it always has.
 */
export function placedFor(call: Pick<ManualCall, "phone" | "lead">): string | null {
  return sameNumber(call.phone, call.lead.phone) ? null : call.lead.id;
}
